// Template campi cartella clinica per prestazione oculistica.
// Ogni template definisce: campi OD/OS aggiuntivi e blocchi extra (esami, terapie specifiche).

// Campi refrazione standard: posizione subito dopo visus corretto.
export const REFRAZIONE_FIELDS = [
  "visus_naturale",
  "visus_corretto",
  "posizione_refrazione",
  "autoref_no_ciclo",
  "autoref_ciclo",
];

// Posizione ora ha solo 3 opzioni; "altro" abilita campo testo libero.
export const POSIZIONE_OPTIONS = [
  { value: "primaria", label: "Primaria" },
  { value: "eccentrica", label: "Eccentrica" },
  { value: "altro", label: "Altro…" },
];

// Metodo misurazione tono / IOP
export const TONO_METODI = [
  { value: "soffio", label: "Soffio (NCT)" },
  { value: "icare", label: "iCare (rimbalzo)" },
  { value: "goldmann", label: "Goldmann (applanazione)" },
];

export const EYE_BASE = [...REFRAZIONE_FIELDS, "tono", "orbita", "annessi_palpebre", "congiuntiva", "cornea", "camera_anteriore", "iride", "pupille", "cristallino", "vitreo", "segmento_anteriore", "fundus", "papilla", "macula", "vasi", "periferia_retinica", "note"];

export const FIELD_LABELS = {
  visus_naturale: "Visus naturale",
  visus_corretto: "Visus corretto",
  autoref_no_ciclo: "Autoref senza cicloplegia",
  autoref_ciclo: "Autoref con cicloplegia",
  posizione_refrazione: "Posizione",
  sfera: "Sfera",
  cilindro: "Cilindro",
  asse: "Asse",
  add: "Add",
  tono: "Tono / IOP (mmHg)",
  segmento_anteriore: "Segmento anteriore",
  fundus: "Fundus / Retina",
  note: "Note",
  // glaucoma
  pachimetria: "Pachimetria (μm)",
  gonioscopia: "Gonioscopia / angolo",
  sinechie: "Sinechie",
  oct_nervo: "OCT nervo ottico / RNFL",
  oct_macula_gcc: "OCT macula / GCC",
  campo_visivo: "Campo visivo computerizzato",
  // retina
  oct_macula: "OCT macula",
  angio_oct: "Angio-OCT",
  retinografia: "Retinografia",
  fluorangiografia: "Fluorangiografia",
  // cataratta
  biometria: "Biometria",
  topografia: "Topografia corneale",
  cristallino: "Cristallino (nucleo/corticale)",
  iol_type: "Tipo IOL (monofocale/torica/multifocale/EDOF)",
  iol_power: "Potere lente (D)",
  // cornea/rifrattiva
  tomografia: "Tomografia corneale",
  aberrometria: "Aberrometria",
  pupille: "Pupille",
  annessi_palpebre: "Annessi / Palpebre",
  orbita: "Orbita",
  congiuntiva: "Congiuntiva",
  cornea: "Cornea",
  camera_anteriore: "Camera anteriore",
  iride: "Iride",
  vitreo: "Vitreo",
  papilla: "Papilla ottica",
  macula: "Macula",
  vasi: "Vasi",
  periferia_retinica: "Periferia retinica",
  // strabismo
  motilita: "Motilità oculare",
  cover_test: "Cover test",
  prismi: "Prismi",
  stereopsi: "Stereopsi",
  lancaster: "Lancaster / Hess",
  // pediatrica
  cicloplegia_farmaco: "Cicloplegia — farmaco",
  refr_cicloplegia: "Refrazione in cicloplegia",
  note_genitori: "Note ai genitori",
  // laser
  tipo_laser: "Tipo laser (YAG / SLT / Argon / micropulsato)",
  occhio_trattato: "Occhio trattato (OD / OS / OU)",
  parametri_laser: "Parametri (energia, spot, durata)",
  consenso: "Consenso firmato",
};

// Esame obiettivo granulare (nuovo layout): usato da tutti gli ambulatori al posto
// del vecchio riquadro unico "segmento_anteriore"/"fundus" (che restano solo come
// campi legacy nascosti, mostrati in nota se contengono dati storici).
export const EO_GRANULARE = ["orbita", "annessi_palpebre", "congiuntiva", "cornea", "camera_anteriore", "iride", "pupille", "cristallino", "vitreo", "segmento_anteriore", "fundus", "papilla", "macula", "vasi", "periferia_retinica"];

export const TEMPLATES = {
  base: {
    label: "Visita base / Prima visita",
    eye: EYE_BASE,
    extra: ["prescrizione_occhiali"],
  },
  glaucoma: {
    label: "Glaucoma",
    eye: ["visus_naturale", "visus_corretto", "posizione_refrazione", "tono", "pachimetria", "gonioscopia", "sinechie", ...EO_GRANULARE, "oct_nervo", "oct_macula_gcc", "campo_visivo", "note"],
    extra: ["terapia_glaucoma", "laser_chirurgia"],
  },
  retina: {
    label: "Retina medica",
    eye: ["visus_naturale", "visus_corretto", "posizione_refrazione", "tono", ...EO_GRANULARE, "oct_macula", "angio_oct", "retinografia", "fluorangiografia", "note"],
    extra: ["terapia_retina"],
  },
  cataratta: {
    label: "Cataratta",
    eye: ["visus_naturale", "visus_corretto", "posizione_refrazione", "tono", ...EO_GRANULARE, "biometria", "topografia", "oct_macula", "iol_type", "iol_power", "note"],
    extra: ["suggerimento_iol"],
  },
  cornea: {
    label: "Cornea",
    eye: [...REFRAZIONE_FIELDS, "tono", "pachimetria", "topografia", "tomografia", "aberrometria", "film_lacrimale", "superficie_oculare", ...EO_GRANULARE, "note"],
    extra: ["idoneita_chirurgica"],
  },
  rifrattiva: {
    label: "Chirurgia rifrattiva",
    eye: [...REFRAZIONE_FIELDS, "tono", "pachimetria", "topografia", "tomografia", "aberrometria", ...EO_GRANULARE, "note"],
    extra: ["idoneita_chirurgica"],
  },
  strabismo: {
    label: "Strabismo / Ortottica",
    eye: [...REFRAZIONE_FIELDS, "motilita", "cover_test", "prismi", "stereopsi", "lancaster", ...EO_GRANULARE, "note"],
    extra: ["piano_ortottico"],
  },
  pediatrica: {
    label: "Pediatrica",
    eye: [...REFRAZIONE_FIELDS, "motilita", "cover_test", "cicloplegia_farmaco", "refr_cicloplegia", ...EO_GRANULARE, "note", "note_genitori"],
    extra: [],
  },
  laser: {
    label: "Laser ambulatoriale",
    eye: ["visus_naturale", "visus_corretto", "posizione_refrazione", "tono", ...EO_GRANULARE, "tipo_laser", "occhio_trattato", "parametri_laser", "consenso", "note"],
    extra: ["controllo_post"],
  },
};

export const EXTRA_LABELS = {
  prescrizione_occhiali: "Prescrizione occhiali",
  terapia_glaucoma: "Terapia glaucoma",
  laser_chirurgia: "Laser / chirurgia suggerita",
  terapia_retina: "Terapia retina (anti-VEGF, fotocoagulazione, etc.)",
  suggerimento_iol: "Suggerimento IOL (IA)",
  idoneita_chirurgica: "Idoneità chirurgica (PRK / LASIK / SMILE / trapianto)",
  piano_ortottico: "Piano ortottico / chirurgico",
  controllo_post: "Controllo post-laser",
};

// Opzioni rapide per Annessi/palpebre e Pupille (separate)
export const ANNESSI_OPTIONS = [
  { value: "sani", label: "Sani" },
  { value: "dermatocalasi", label: "Dermatocalasi" },
  { value: "neoformazioni", label: "Neoformazioni" },
  { value: "blefarite", label: "Blefarite" },
  { value: "ptosi", label: "Ptosi" },
  { value: "ectropion", label: "Ectropion" },
  { value: "entropion", label: "Entropion" },
  { value: "altro", label: "Altro…" },
];

export const PUPILLE_OPTIONS = [
  { value: "eucicliche", label: "Eucicliche, isocoriche, normoreagenti" },
  { value: "anisocoria", label: "Anisocoria" },
  { value: "discoria", label: "Discoria" },
  { value: "rapd", label: "RAPD positivo" },
  { value: "midriasi_farmacologica", label: "Midriasi farmacologica" },
  { value: "altro", label: "Altro…" },
];

// Esami diagnostici aggiungibili a qualunque visita.
// IMPORTANTE: questi id devono allinearsi (quando possibile) ai key dei blocchi diagnostica
// in clinic_templates.py per consentire al medico di pre-attivarli dai modelli.
export const DIAGNOSTICA_OPTIONS = [
  { id: "oct_macula", label: "OCT macula" },
  { id: "oct_rnfl", label: "OCT RNFL / Nervo ottico / GCC" },
  { id: "oct_segmento_anteriore", label: "OCT segmento anteriore / Angolo" },
  { id: "angio_oct", label: "Angio-OCT (OCT-A)" },
  { id: "retinografia", label: "Retinografia" },
  { id: "campo_visivo", label: "Campo visivo" },
  { id: "topografia_corneale", label: "Topografia / Tomografia corneale" },
  { id: "biometria", label: "Biometria (per IOL)" },
  { id: "pachimetria", label: "Pachimetria" },
  { id: "microscopia_endoteliale", label: "Microscopia / Conta endoteliale" },
  { id: "fluorangiografia", label: "Fluorangiografia (FA)" },
  { id: "ecografia", label: "Ecografia oculare (A/B-scan)" },
  { id: "aberrometria", label: "Aberrometria" },
  { id: "pupillometria", label: "Pupillometria" },
  { id: "amsler", label: "Test di Amsler" },
  { id: "schirmer_but", label: "Test lacrimali (Schirmer/BUT)" },
  { id: "diag_hess_lancaster", label: "Schermo di Hess / Lancaster" },
  { id: "diag_gaze9_foto", label: "Foto Gaze9 (9 posizioni di sguardo)" },
  { id: "luci_worth", label: "Luci di Worth (referto)" },
  { id: "stereopsi", label: "Stereopsi (referto)" },
  { id: "cover_test", label: "Cover test (referto)" },
  { id: "yag_capsulotomia", label: "YAG capsulotomia (prestazione)" },
  { id: "slt_argon_micropulsato", label: "SLT / Argon / Micropulsato (prestazione)" },
  { id: "allegati", label: "Immagini / Allegati (PDF/JPG)" },
];

// Alias service ufficiali/legacy → layout campi occhio (TEMPLATES key)
const SERVICE_LAYOUT_ALIASES = {
  generale: "base", visita: "base",
  retina_medica: "retina", maculopatia: "retina", diabetica: "retina",
  fluorangiografia: "retina", oct: "retina",
  refrattiva: "rifrattiva", cornea_rifrattiva: "rifrattiva",
  ortottica: "strabismo", strabismo_ortottica: "strabismo",
  glaucoma_visit: "glaucoma", glaucoma_chirurgia: "glaucoma",
  pediatrico: "pediatrica",
  chirurgia: "cataratta",
  postoperatorio: "base", urgenze: "base", annessi: "base",
};

export function templateForService(service) {
  if (!service) return TEMPLATES.base;
  return TEMPLATES[service] || TEMPLATES[SERVICE_LAYOUT_ALIASES[service]] || TEMPLATES.base;
}
